import { CSSProperties, PureComponent, ReactNode } from 'react'
import SwipeableViews from 'react-swipeable-views'
import { mod } from 'react-swipeable-views-core'

type SlideRendererArgs = {
  index: number
  key: number
}

type VirtualizedSwipeableViewsProps = {
  className?: string
  style?: CSSProperties
  containerStyle?: CSSProperties
  slideStyle?: CSSProperties
  slideClassName?: string
  threshold?: number
  animateHeight?: boolean
  animateTransitions?: boolean
  axis?: 'x' | 'x-reverse' | 'y' | 'y-reverse'
  disabled?: boolean
  enableMouseEvents?: boolean
  hysteresis?: number
  ignoreNativeScroll?: boolean
  resistance?: boolean
  springConfig?: {
    duration: string
    easeFunction: string
    delay: string
  }
  index?: number
  slideCount?: number
  overscanSlideBefore?: number
  overscanSlideAfter?: number
  slideRenderer: (args: SlideRendererArgs) => ReactNode
  onChangeIndex?: (index: number, indexLatest: number) => void
  onSwitching?: (index: number, type: 'move' | 'end') => void
  onTransitionEnd?: () => void
}

type VirtualizedSwipeableViewsState = {
  index: number
  indexContainer: number
  indexStart: number
  indexStop: number
}

class VirtualizedSwipeableViews extends PureComponent<
  VirtualizedSwipeableViewsProps,
  VirtualizedSwipeableViewsState
> {
  static defaultProps = {
    overscanSlideAfter: 2,
    overscanSlideBefore: 3,
  }

  private timer: ReturnType<typeof setTimeout> | null = null

  state: VirtualizedSwipeableViewsState = {
    index: this.props.index || 0,
    indexContainer: 0,
    indexStart: 0,
    indexStop: 0,
  }

  componentDidMount () {
    this.setWindow(this.state.index)
  }

  componentDidUpdate (prevProps: VirtualizedSwipeableViewsProps) {
    const { index } = this.props

    if (typeof index === 'number' && index !== prevProps.index && typeof prevProps.index === 'number') {
      const indexDiff = index - prevProps.index
      this.setIndex(index, this.state.indexContainer + indexDiff, indexDiff)
    }
  }

  componentWillUnmount () {
    if (this.timer) {
      clearTimeout(this.timer)
    }
  }

  private setIndex (index: number, indexContainer: number, indexDiff: number) {
    const nextState: VirtualizedSwipeableViewsState = {
      index,
      indexContainer,
      indexStart: this.state.indexStart,
      indexStop: this.state.indexStop,
    }

    if (indexDiff > 0 && (!this.props.slideCount || nextState.indexStop < this.props.slideCount - 1)) {
      nextState.indexStop += 1
    }

    if (index > nextState.indexStop) {
      nextState.indexStop = index
    }

    const beforeAhead = nextState.indexStart - index
    if (beforeAhead > 0) {
      nextState.indexContainer += beforeAhead
      nextState.indexStart -= beforeAhead
    }

    this.setState(nextState)
  }

  private setWindow (index = this.state.index) {
    const { slideCount, overscanSlideBefore = 3, overscanSlideAfter = 2 } = this.props
    let beforeAhead = overscanSlideBefore
    let afterAhead = overscanSlideAfter

    if (slideCount) {
      if (beforeAhead > index) {
        beforeAhead = index
      }

      if (afterAhead + index > slideCount - 1) {
        afterAhead = slideCount - index - 1
      }
    }

    this.setState({
      indexContainer: beforeAhead,
      indexStart: index - beforeAhead,
      indexStop: index + afterAhead,
    })
  }

  private handleChangeIndex = (indexContainer: number, indexLatest: number) => {
    const { slideCount, onChangeIndex } = this.props
    const indexDiff = indexContainer - indexLatest
    let index = this.state.index + indexDiff

    if (slideCount) {
      index = mod(index, slideCount)
    }

    if (this.props.index === undefined) {
      this.setIndex(index, indexContainer, indexDiff)
    }

    if (onChangeIndex) {
      onChangeIndex(index, this.state.index)
    }
  }

  private handleSwitching = (indexContainer: number, type: 'move' | 'end') => {
    const { slideCount, onSwitching } = this.props
    const { indexStart } = this.state
    let index = indexContainer + indexStart

    if (slideCount) {
      index = mod(index, slideCount)
    }

    if (onSwitching) {
      onSwitching(index, type)
    }
  }

  private handleTransitionEnd = () => {
    this.timer = setTimeout(() => {
      this.setWindow()
    }, 0)

    if (this.props.onTransitionEnd) {
      this.props.onTransitionEnd()
    }
  }

  render () {
    const {
      slideRenderer,
      className,
      style,
      containerStyle,
      slideStyle,
      slideClassName,
      threshold,
      animateHeight,
      animateTransitions,
      axis,
      disabled,
      enableMouseEvents,
      hysteresis,
      ignoreNativeScroll,
      resistance,
      springConfig,
    } = this.props
    const { indexContainer, indexStart, indexStop } = this.state
    const slides: ReactNode[] = []

    for (let slideIndex = indexStart; slideIndex <= indexStop; slideIndex += 1) {
      slides.push(slideRenderer({ index: slideIndex, key: slideIndex }))
    }

    return (
      <SwipeableViews
        className={ className }
        style={ style }
        containerStyle={ containerStyle }
        slideStyle={ slideStyle }
        slideClassName={ slideClassName }
        threshold={ threshold }
        animateHeight={ animateHeight }
        animateTransitions={ animateTransitions }
        axis={ axis }
        disabled={ disabled }
        enableMouseEvents={ enableMouseEvents }
        hysteresis={ hysteresis }
        ignoreNativeScroll={ ignoreNativeScroll }
        resistance={ resistance }
        springConfig={ springConfig }
        index={ indexContainer }
        onChangeIndex={ this.handleChangeIndex }
        onSwitching={ this.handleSwitching }
        onTransitionEnd={ this.handleTransitionEnd }
      >
        { slides }
      </SwipeableViews>
    )
  }
}

export default VirtualizedSwipeableViews
